import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axiosRetry from "axios-retry";
import { AlertConstants, useAlerts } from "best-common-react";
import React, { useEffect } from "react";
import { LocalStorageConstants } from "../constants/LocalStorageConstants";
import { useUser } from "../contexts/UserContext";
import { AlertDTO } from "../types/ApiTypes";

const HttpInterceptor: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { logout } = useUser();
  const { addAlert } = useAlerts();

  const createResponseAlerts = (alerts: AlertDTO[]) => {
    alerts.forEach((alert: AlertDTO) => {
      addAlert({
        type: !!alert.severity ? alert.severity : AlertConstants.TYPES.WARNING,
        text: !!alert.message ? alert.message : "Error: please contact support",
      });
    });
  };

  const createInterceptors = () => {
    axiosRetry(axios, {
      retries: 3, //attempts to retry
      retryCondition: (error) => {
        console.log("Error fetching from API will retry...");
        return !!error?.response && error.response.status === 401;
      }, // only retry on unauthenticated errors to prevent retrying 500s unless that is wanted
      retryDelay: (retryCount) => retryCount * 1000, // wait 1 second between retrys
    });

    axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const oktaTokenStr =
        localStorage.getItem(LocalStorageConstants.OKTA_TOKEN) || '{"accessToken": "", "idToken": "" }';

      const { accessToken = {}, idToken = {} } = JSON.parse(oktaTokenStr);

      if (config.url.indexOf("/oauth/token") < 0 || config.headers.addToken) {
        config.headers.Authorization = `Bearer ${accessToken.accessToken}`;
        config.headers["x-id-token"] = idToken.idToken;
      }
      return config;
    });

    axios.interceptors.response.use(
      (resp: AxiosResponse) => {
        if (!resp) return null;
        if (resp.data) {
          if (resp.data.alerts) {
            createResponseAlerts(resp.data.alerts);
          }
        }
        return resp;
      },
      (err: AxiosError) => {
        if (!!err.response && err.response.status === 401) {
          logout();
          // @ts-ignore
        } else if (!!err?.response?.data?.alerts) {
          // @ts-ignore
          createResponseAlerts(err.response.data.alerts);
        }
        return Promise.reject(err);
      },
    );
  };

  useEffect(() => {
    createInterceptors();
  }, []);

  return <>{children}</>;
};

export default HttpInterceptor;
